// src/pages/taxonomy/OrderCategories/OrderCategoriesListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrderCategoriesAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';

const includesCi = (h = '', n = '') =>
  (h || '').toString().toLowerCase().includes((n || '').toString().toLowerCase());

const fmtIDR = (n) => n == null ? '-' : `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

function getCategoryLabel(r = {}) {
  return r.category_name ?? r.category?.name ?? r.category ?? '-';
}
function getSizeLabel(r = {}) {
  return r.size_name ?? r.size?.name ?? r.size ?? '-';
}
function getMaterialLabel(r = {}) {
  return r.material_name ?? r.material?.name ?? r.material ?? '-';
}

export default function OrderCategoriesListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filters + pagination
  const [qName, setQName] = useState('');
  const [qCode, setQCode] = useState('');
  const [qCategory, setQCategory] = useState('');
  const [qSize, setQSize] = useState('');
  const [qMaterial, setQMaterial] = useState('');
  const [per, setPer] = useState(25);
  const [page, setPage] = useState(1);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await OrderCategoriesAPI.list();
      const items = data?.items ?? data?.data ?? (Array.isArray(data) ? data : []);
      setRows(items || []);
    } catch (e) {
      setError(e.message || 'Failed to fetch order categories');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // filter
  const filtered = useMemo(() => {
    let r = rows;
    if (qName)     r = r.filter(x => includesCi(x.name, qName));
    if (qCode)     r = r.filter(x => includesCi(x.code, qCode));
    if (qCategory) r = r.filter(x => includesCi(getCategoryLabel(x), qCategory));
    if (qSize)     r = r.filter(x => includesCi(getSizeLabel(x), qSize));
    if (qMaterial) r = r.filter(x => includesCi(getMaterialLabel(x), qMaterial));
    return r;
  }, [rows, qName, qCode, qCategory, qSize, qMaterial]);

  // pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(start, start + per), [filtered, start, per]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this category?')) return;
    try {
      await OrderCategoriesAPI.destroy(id);
      const nextRows = rows.filter(r => r.id !== id);
      setRows(nextRows);

      const nextFiltered = nextRows.filter(x =>
        (!qName     || includesCi(x.name, qName)) &&
        (!qCode     || includesCi(x.code, qCode)) &&
        (!qCategory || includesCi(getCategoryLabel(x), qCategory)) &&
        (!qSize     || includesCi(getSizeLabel(x), qSize)) &&
        (!qMaterial || includesCi(getMaterialLabel(x), qMaterial))
      );
      const nextTotalPages = Math.max(1, Math.ceil(nextFiltered.length / per));
      if (pageSafe > nextTotalPages) setPage(nextTotalPages);
    } catch (e) {
      alert(e.message);
    }
  }

  function resetFilters() {
    setQName('');
    setQCode('');
    setQCategory('');
    setQSize('');
    setQMaterial('');
    setPage(1);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h3 className="mb-0">Order Categories</h3>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ width: 200 }}
            placeholder="Filter name…"
            value={qName}
            onChange={(e) => { setQName(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 140 }}
            placeholder="Filter code…"
            value={qCode}
            onChange={(e) => { setQCode(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 180 }}
            placeholder="Filter category…"
            value={qCategory}
            onChange={(e) => { setQCategory(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 140 }}
            placeholder="Filter size…"
            value={qSize}
            onChange={(e) => { setQSize(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 180 }}
            placeholder="Filter material…"
            value={qMaterial}
            onChange={(e) => { setQMaterial(e.target.value); setPage(1); }}
          />

          <select
            className="form-select"
            style={{ width: 120 }}
            value={per}
            onChange={(e) => { setPer(parseInt(e.target.value, 10)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>

          <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
          <Link className="btn btn-primary" to={`${BASE}/order_categories/new`}>New</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 140 }}>Code</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Material</th>
                  <th className="text-end" style={{ width: 140 }}>Base Price</th>
                  <th style={{ width: 80 }}>Active</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length ? paged.map(r => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.name}</td>
                    <td><code>{r.code}</code></td>
                    <td>{getCategoryLabel(r)}</td>
                    <td>{getSizeLabel(r)}</td>
                    <td>{getMaterialLabel(r)}</td>
                    <td className="text-end">{fmtIDR(r.base_price)}</td>
                    <td>{r.is_active ? '✅' : '❌'}</td>
                    <td>
                      <Link className="btn btn-link p-0 me-3" to={`${BASE}/order_categories/${r.id}/edit`}>Edit</Link>
                      <button className="btn btn-link text-danger p-0" onClick={() => handleDelete(r.id)}>Delete</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="text-muted text-center">No categories found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">Total: {filtered.length} • Page {pageSafe} / {totalPages}</div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(1)}>&laquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>&lsaquo; Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next &rsaquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(totalPages)}>&raquo;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
